import './day-picker-styles.css'

import { ChangeEvent, memo, useCallback, useMemo, useState } from 'react'

import { CalendarMonth, Clear } from '@mui/icons-material'
import {
  IconButton,
  IconButtonProps,
  InputAdornment,
  InputBase,
  InputBaseProps,
  Popover,
  styled,
  Typography,
  useTheme,
} from '@mui/material'
import { format, getHours, getMinutes, isValid, isWithinInterval, parse, set } from 'date-fns'
import { DayPicker } from 'react-day-picker'

import { validEndDate, validStartDate } from '@shared/utils/support'

const dateFormat = 'dd.MM.yyyy' as const

const DateInput = styled(InputBase)(({ theme }) => ({
  ...theme.typography['heading-base'],
  lineHeight: 1,
  minWidth: 160,
}))

type DatePickerProps = {
  onChange: (date: Date | null) => void
  /**
   * Component internally works with the format **ISO 8601**
   * @defaultValue `new Date(new Date(Date.now()))`
   */
  defaultDate?: Date
  error?: boolean
  PickerIconProps?: Pick<IconButtonProps, 'size' | 'sx'>
}

const END_MONTH = new Date(new Date().getFullYear() + 5, 11, 31)

const DatePickerRaw = ({
  defaultDate,
  onChange,
  error,
  PickerIconProps,

  ...inputBaseProps
}: DatePickerProps & Omit<InputBaseProps, 'children' | 'error' | 'onChange'>) => {
  const theme = useTheme()

  const derivedDefaultDate = useMemo(() => defaultDate ?? new Date(Date.now()), [defaultDate])

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>()

  const [textFieldValue, setTextFieldValue] = useState(() =>
    defaultDate !== undefined ? format(defaultDate, dateFormat) : '',
  )
  const [localError, setlocalError] = useState(false)

  const handleDayPickerChange = useCallback(
    (date?: Date) => {
      if (date) {
        const parsedDateTime = set(date, {
          hours: getHours(derivedDefaultDate),
          minutes: getMinutes(derivedDefaultDate),
        })

        setlocalError(false)

        setTextFieldValue(format(parsedDateTime, dateFormat))

        onChange(parsedDateTime)
      } else {
        setTextFieldValue('')
      }
    },
    [derivedDefaultDate, onChange],
  )

  const handleTextfieldBlur = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const parsedDate = parse(e.currentTarget.value, dateFormat, derivedDefaultDate)
      if (isValid(parsedDate) && isWithinInterval(parsedDate, { start: validStartDate, end: validEndDate })) {
        const parsedDateTime = set(parsedDate, {
          hours: getHours(derivedDefaultDate),
          minutes: getMinutes(derivedDefaultDate),
        })

        setlocalError(false)

        setTextFieldValue(format(parsedDateTime, dateFormat))

        onChange(parsedDateTime)
      } else {
        setlocalError(!!e.currentTarget.value.length)

        onChange(null)
      }
    },
    [derivedDefaultDate, onChange],
  )

  const handleClear = useCallback(() => {
    setTextFieldValue('')

    setlocalError(false)

    onChange(null)
  }, [onChange])

  return (
    <DateInput
      {...inputBaseProps}
      value={textFieldValue}
      onChange={useCallback((e: ChangeEvent<HTMLInputElement>) => setTextFieldValue(e.currentTarget.value), [])}
      onBlur={handleTextfieldBlur}
      type="text"
      placeholder={dateFormat}
      inputProps={{
        style: {
          color: localError || error ? theme.palette.error.main : 'initial',
        },
      }}
      endAdornment={
        <InputAdornment sx={{ position: 'relative' }} position="end">
          {!!textFieldValue.length && (
            <IconButton size="small" onClick={handleClear}>
              <Clear fontSize="inherit" />
            </IconButton>
          )}
          <IconButton {...PickerIconProps} onClick={(e) => setAnchorEl(e.currentTarget)}>
            <CalendarMonth fontSize="inherit" />
          </IconButton>
          <Popover
            open={!!anchorEl}
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            onClose={() => setAnchorEl(undefined)}
            data-theme={theme.palette.mode}
          >
            <DayPicker
              style={{ padding: theme.spacing(1, 1.5) }}
              animate
              mode="single"
              selected={
                textFieldValue.length && isValid(parse(textFieldValue, dateFormat, derivedDefaultDate))
                  ? parse(textFieldValue, dateFormat, derivedDefaultDate)
                  : undefined
              }
              onSelect={handleDayPickerChange}
              showOutsideDays
              captionLayout="dropdown"
              endMonth={END_MONTH}
              components={{
                MonthCaption: (props) => <Typography {...props} variant="heading-base" component="div" />,
                PreviousMonthButton: (props) => <button style={{ width: 18, marginRight: 16 }} {...props} />,
                NextMonthButton: (props) => <button style={{ width: 18, marginRight: 8 }} {...props} />,
              }}
            />
          </Popover>
        </InputAdornment>
      }
    />
  )
}

export const DatePicker = memo(DatePickerRaw)
